import clsx from 'clsx';
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';
import {getCountries, getLanguages, getOrderTypes, getPortalInfo, getUserDetails} from '../../../shared/utils';
import {clearCustomers, getCommercialCustomers} from '../../../store/actions/customerActions';
import {
    calculatePricing,
    generateOrder,
    setAddress,
    setCap,
    setCity,
    setCompany,
    setCountry,
    setFirstName,
    setLanguage,
    setLastName,
    setOrderAction,
    setOrderNote,
    setOrderRef,
    setOrderType,
    setPricing,
    setUsername,
    setVat
} from '../../../store/actions/orderActions';
import initialState from '../../../store/reducers/initialState';
import Confirm from '../../Confirm/Confirm';
import PartnerBadge from '../../PartnerBadge/PartnerBadge';
import FormattedPrice from './FormattedPrice';
import classes from './Summary.module.scss';

const EXISTING_CUSTOMER_ORDER_TYPES = [
    'renewalUpgradeStandard',
    'renewalUpgradeDiscount',
    'oneTimePurchase',
    'professionalServiceExisting'
];

const isExistingCustomerOrderType = orderType => EXISTING_CUSTOMER_ORDER_TYPES.includes(orderType);

const getCustomerCompany = customer => {
    if (customer.companyName) {
        return customer.companyName;
    }

    if (customer.company) {
        return customer.company;
    }

    if (customer.customer) {
        return customer.customer;
    }

    if (customer.name) {
        return customer.name;
    }

    if (customer.products && customer.products.length > 0) {
        const description = customer.products[ 0 ].description || '';
        const match = description.match(/(.+\s-\scliente finale\s?)(.+)/i);
        if (match && match.length === 3) {
            return match[ 2 ].trim();
        }
    }

    return '';
};

const getCustomerEmail = customer => customer.email || customer.aidaUsername || customer.username || customer.contactEmail || '';

const getCustomerValue = customer => customer.id == null ? `${getCustomerEmail(customer)}|${getCustomerCompany(customer)}` : String(customer.id);

const getCustomerVat = customer => customer.vat || customer.vatNumber || '';

const getCustomerAddress = customer => customer.address || '';

const getCustomerCity = customer => customer.city || '';

const getCustomerCap = customer => customer.cap || customer.postalCode || '';

const getCustomerCountry = customer => customer.country || customer.countryCode || '';

const getLanguageForCountry = country => {
    switch (country) {
        case 'IT':
            return 'IT';
        case 'FR':
            return 'FR';
        case 'ES':
            return 'ES';
        case 'PT':
            return 'PT';
        case 'GB':
        case 'US':
            return 'EN';
        case 'DE':
            return 'DE';
        default:
            return '';
    }
};

const getCustomerLanguage = customer => customer.language || getLanguageForCountry(getCustomerCountry(customer));

const getSortedCustomerOptions = customers => {
    const options = [];
    const seen = new Set();
    const entities = customers.entities || {};
    const result = customers.result || [];

    result.forEach(id => {
        const customer = entities[ id ];
        if (!customer) {
            return;
        }

        const company = getCustomerCompany(customer);
        const email = getCustomerEmail(customer);
        const value = getCustomerValue(customer);
        if (!company || seen.has(value)) {
            return;
        }

        seen.add(value);
        options.push({
            value,
            label: email ? `${company} (${email})` : company,
            customer
        });
    });

    return options.sort((left, right) => left.label.localeCompare(right.label));
};

const Summary = ({ isSubmitting, setSubmitting }) => {
    const order = useSelector(state => state.order.data);

    const username = useSelector(state => state.order.username);
    const company = useSelector(state => state.order.company);
    const city = useSelector(state => state.order.city);
    const cap = useSelector(state => state.order.cap);
    const address = useSelector(state => state.order.address);
    const vat = useSelector(state => state.order.vat);
    const firstName = useSelector(state => state.order.firstName);
    const lastName = useSelector(state => state.order.lastName);
    const language = useSelector(state => state.order.language);
    const country = useSelector(state => state.order.country);
    const orderRef = useSelector(state => state.order.orderRef);
    const orderNote = useSelector(state => state.order.orderNote);
    const orderType = useSelector(state => state.order.orderType);

    const isCalculating = useSelector(state => state.order.calculating);
    const priceOneOff = useSelector(state => state.order.priceOneOff) ?? 0;
    const priceYear = useSelector(state => state.order.priceYear) ?? 0;
    const price = useSelector(state => state.order.price) ?? 0;
    const currency = useSelector(state => state.ui.currency);
    const user = useSelector(state => state.auth.user);
    const customers = useSelector(state => state.customer.data);
    const dispatch = useDispatch();
    const history = useHistory();
    const [ usernameError, setUsernameError ] = useState('');
    const [ loadingCustomers, setLoadingCustomers ] = useState(false);
    const [ selectedCustomerValue, setSelectedCustomerValue ] = useState('');
    const isBase = getUserDetails(user).level === 'BASE';
    const hasOrderType = orderType.length > 0;
    const isExistingCustomerOrder = isExistingCustomerOrderType(orderType);
    const customerOptions = getSortedCustomerOptions(customers);
    const selectedCustomerOption = customerOptions.find(option => option.value === selectedCustomerValue);
    const partnerThirdparty = {
        name: user?.partnerCompanyName || '',
        vat: user?.partnerVatNumber || '',
        country: user?.partnerCountryCode || ''
    };

    console.log("Rendering Summary priceYear", priceYear);

    const requestThirdpartyInfo = !user || !user.erpId || Number(user.erpId) <= 0;

    useEffect(() => {
        if (!user) {
            return;
        }

        if (username.toLowerCase() === user.email.toLowerCase()) {
            setUsernameError('Insert customer\'s email, not yours');
        }
        else {
            setUsernameError('');
        }
    }, [ username, user ]);

    const loadCommercialCustomerOptions = useCallback(() => {
        dispatch(clearCustomers());
        setLoadingCustomers(true);
        return dispatch(getCommercialCustomers()).catch(error => {
            window.Sentry.captureException(error);
            toast.error('Could not load customers for this partner');
            dispatch(clearCustomers());
        }).finally(() => {
            setLoadingCustomers(false);
        });
    }, [ dispatch ]);

    useEffect(() => {
        if (!isExistingCustomerOrder || !user) {
            return;
        }

        loadCommercialCustomerOptions();
    }, [ isExistingCustomerOrder, loadCommercialCustomerOptions, user ]);

    const displayValue = value => {
        if (value == null || String(value).trim().length === 0) {
            return 'Not available';
        }
        return value;
    };

    const displayCountry = countryCode => {
        if (!countryCode) {
            return 'Not available';
        }

        const country = getCountries().find(option => option.value === countryCode);
        return country ? country.displayValue : countryCode;
    };

    const handleGenerate = () => {
        if (isSubmitting) {
            return;
        }

        const handleError = error => {
            window.Sentry.captureException(error);
            setSubmitting(false);
            toast.error(`An error happened while generating your order, please try after reloading the page or contact us at ${getPortalInfo().email}`);
        };

        setSubmitting(true);

        try {
            const erpId = user.erpId;
            dispatch(generateOrder(order, currency, user.email, erpId, firstName, lastName, username, company, vat, address, city, cap, country, language, orderRef, orderNote, partnerThirdparty, orderType)).then(() => {
                setSubmitting(false);
                history.push('/order/success');
                dispatch(setOrderAction({ ...initialState.order.data }));
                dispatch(setUsername(''));
                dispatch(setPricing({ price: -1, priceYear: -1, priceOneOff: -1 }));
                dispatch(setOrderRef(''));
                dispatch(setCompany(''));
                dispatch(setVat(''));
                dispatch(setFirstName(''));
                dispatch(setLastName(''));
                dispatch(setCap(''));
                dispatch(setCity(''));
                dispatch(setAddress(''));
                dispatch(setOrderNote(''));
                dispatch(setOrderType(''));
            }).catch(handleError);
        }
        catch (error) {
            handleError(error);
        }
    };

    const handleSetUsername = e => {
        const { value } = e.target;
        dispatch(setUsername(value));
    };

    const handleSetCompany = (e) => {
        dispatch(setCompany(e.target.value));
    };

    const handleSetVat = (e) => {
        dispatch(setVat(e.target.value));
    };

    const handleSetFirstName = (e) => {
        dispatch(setFirstName(e.target.value));
    };

    const handleSetLastName = (e) => {
        dispatch(setLastName(e.target.value));
    };

    const clearCustomerFields = () => {
        dispatch(setUsername(''));
        dispatch(setCompany(''));
        dispatch(setVat(''));
        dispatch(setFirstName(''));
        dispatch(setLastName(''));
        dispatch(setAddress(''));
        dispatch(setCity(''));
        dispatch(setCap(''));
        dispatch(setCountry(''));
        dispatch(setLanguage(''));
    };

    const handleSetOrderType = (e) => {
        const orderType = e.target.value;
        dispatch(setOrderType(orderType));
        if (!isExistingCustomerOrderType(orderType)) {
            setSelectedCustomerValue('');
            if (isExistingCustomerOrder) {
                clearCustomerFields();
            }
        }

        if (orderType === 'oneTimePurchase') {
            dispatch(setOrderAction({
                ...order,
                duration: 1
            }));
        }
    };

    const handleSetExistingCustomer = e => {
        const value = e.target.value;
        setSelectedCustomerValue(value);

        const option = customerOptions.find(option => option.value === value);
        if (!option) {
            clearCustomerFields();
            return;
        }

        const customer = option.customer;
        dispatch(setUsername(getCustomerEmail(customer)));
        dispatch(setCompany(getCustomerCompany(customer)));
        dispatch(setVat(getCustomerVat(customer)));
        dispatch(setFirstName(customer.firstName || customer.firstname || customer.contactFirstName || ''));
        dispatch(setLastName(customer.lastName || customer.lastname || customer.contactLastName || ''));
        dispatch(setAddress(getCustomerAddress(customer)));
        dispatch(setCity(getCustomerCity(customer)));
        dispatch(setCap(getCustomerCap(customer)));
        dispatch(setCountry(getCustomerCountry(customer)));
        dispatch(setLanguage(getCustomerLanguage(customer)));
    };

    const handleReloadCustomers = () => {
        if (loadingCustomers || isSubmitting) {
            return;
        }

        loadCommercialCustomerOptions();
    };

    const handleSetCap = (e) => {
        dispatch(setCap(e.target.value));
    };

    const handleSetAddress = (e) => {
        dispatch(setAddress(e.target.value));
    };

    const handleSetCity = (e) => {
        dispatch(setCity(e.target.value));
    };

    const handleSetLanguage = (e) => {
        dispatch(setLanguage(e.target.value));
    };

    const handleSetCountry = (e) => {
        const country = e.target.value;
        dispatch(setCountry(country));
        switch (country) {
            case 'IT':
                dispatch(setLanguage('IT'));
                break;
            case 'FR':
                dispatch(setLanguage('FR'));
                break;
            case 'ES':
                dispatch(setLanguage('ES'));
                break;
            case 'PT':
                dispatch(setLanguage('PT'));
                break;
            case 'GB':
            case 'US':
                dispatch(setLanguage('EN'));
                break;
            case 'DE':
                dispatch(setLanguage('DE'));
                break;
            default:
                break;
        }
    };

    const handleSetOrderRef = (e) => {
        dispatch(setOrderRef(e.target.value));
    };

    const handleSetOrderNote = (e) => {
        dispatch(setOrderNote(e.target.value));
    };

    useEffect(() => {
        if (!!order.deployment && !!order.product && user) {
            dispatch(calculatePricing(order, currency, user.email, orderType));
        }
    }, [ order, dispatch, currency, user, orderType ]);

    const isValid = (priceOneOff > -1 && priceYear > -1 && price > -1) || isCalculating;

    const usernameIsValid = username.length > 0 && usernameError === '';

    const thirdpartyIsValid = requestThirdpartyInfo ? partnerThirdparty.name.length > 0 && partnerThirdparty.vat.length > 0 && partnerThirdparty.country.length > 0 : true;

    const customerInfoIsValid = isExistingCustomerOrder ?
        !!selectedCustomerOption && usernameIsValid && company.length > 0 :
        usernameIsValid && company.length > 0 && firstName.length > 0 && country.length > 0 && language.length > 0;

    let aidaLevel = 'Enterprise Plus';
    if (order.pages === 'AIDA-ent3') {
        aidaLevel = 'Business';
    }
    else if (order.pages === 'AIDA-ent4') {
        aidaLevel = 'Business';
    }
    else if (order.pages === 'AIDA-ent5') {
        aidaLevel = 'Enterprise';
    }
    else if (order.pages === '-') {
        aidaLevel = '---';
    }

    return <div className={clsx('box', classes.Summary)}>
        <h4>Summary</h4>
        {isValid && <ul>
            <li>AIDA level: <span className="tag is-primary">{aidaLevel}</span></li>
            <li>Price (one-off): <FormattedPrice price={priceOneOff} currency={currency} loading={isCalculating} /></li>
            <li>Price (yearly): <FormattedPrice price={priceYear} currency={currency} loading={isCalculating} /></li>
            <li>
                <hr />
            </li>
            <li className="has-text-weight-bold">Price (total): <FormattedPrice price={price} currency={currency}
                                                                                loading={isCalculating} /></li>
        </ul>}
        {!isValid &&
            <div className="notification is-info is-light">Once you've selected your desired product and options to the
                                                           left, pricing information will appear here.</div>}
        {isValid && <>
            <div className={classes.Notice}>Generated price includes your exclusive <PartnerBadge
                showIcon={false} /> partner level discount
            </div>
            <hr />
            <p style={{ fontSize: '0.9rem' }}>Enter the end customer's details under which you intend to
                                              create the
                                              account. Fields marked with * are required:</p>
            <h5>Your information</h5>
            <div className="field">
                <label className="label">Your account</label>
                <p className={classes.StaticValue}>{displayValue(user?.email)}</p>
            </div>
            <div className="field">
                <label className="label">Your company name</label>
                <p className={classes.StaticValue}>{displayValue(partnerThirdparty.name)}</p>
            </div>
            <div className="field">
                <label className="label">Your company VAT number</label>
                <p className={classes.StaticValue}>{displayValue(partnerThirdparty.vat)}</p>
            </div>
            <div className="field">
                <label className="label">Your country</label>
                <p className={classes.StaticValue}>{displayCountry(partnerThirdparty.country)}</p>
            </div>
            {requestThirdpartyInfo && !thirdpartyIsValid &&
                <p className="help is-danger">
                    Your partner profile is missing company, VAT, or country. Please contact <a
                        href="https://www.aidacloud.com/support/contacts/"
                        target="_blank"
                        rel="noopener noreferrer">support</a> before generating an order.
                </p>}
            <h5>Customer/Order information</h5>
            <div className="field">
                <label className="label">Order type *</label>
                <div className="control has-icons-left">
                    <div className={clsx('select is-small is-block', hasOrderType && 'is-success')}>
                        <select value={orderType} onChange={handleSetOrderType} disabled={isSubmitting}>
                            <option value="">Select order type</option>
                            {getOrderTypes().map(o => <option key={`country-${o.value}`}
                                                              value={o.value}>{o.displayValue}</option>)}
                        </select>
                        <div className="icon is-small is-left">
                            <i className="fas fa-file-contract" />
                        </div>
                    </div>
                </div>
                {orderType === 'oneTimePurchase' &&
                    <p className={clsx('help is-info', classes.ContractDurationNotice)}>One-time page purchases use a fixed 1-year contract duration.</p>}
            </div>
            {!hasOrderType && <div className={clsx('notification', 'is-info', 'is-light', classes.OrderTypeNotice)}>
                <div className={classes.OrderTypeNoticeIcon}>
                    <i className="fas fa-file-contract" />
                </div>
                <div>
                    <strong>Select an order type to continue.</strong>
                    <p>Additional order details will appear after your selection.</p>
                </div>
            </div>}
            {hasOrderType && <>
                <a href="/web/renewals" target="_blank" className={classes.RenewalRulesLink}
                   rel="noopener noreferrer"><i
                    className="fas fa-external-link" />&nbsp;Rules for existing customers</a>
                {isExistingCustomerOrder && <div className="field">
                    <label className="label">Customer *</label>
                    <div className={clsx('field has-addons', classes.CustomerSelectField)}>
                        <div className="control is-expanded has-icons-left">
                            <div className={clsx('select is-small is-fullwidth', selectedCustomerValue.length > 0 && 'is-success')}>
                                <select value={selectedCustomerValue}
                                        onChange={handleSetExistingCustomer}
                                        disabled={isSubmitting || loadingCustomers}>
                                    <option value="">{loadingCustomers ? 'Loading customers...' : 'Select customer'}</option>
                                    {customerOptions.map(option => <option key={`customer-${option.value}`}
                                                                           value={option.value}>{option.label}</option>)}
                                </select>
                                <div className="icon is-small is-left">
                                    <i className="fas fa-building" />
                                </div>
                            </div>
                        </div>
                        <div className="control">
                            <button type="button"
                                    className={clsx('button is-small', classes.CustomerReloadButton)}
                                    onClick={handleReloadCustomers}
                                    disabled={isSubmitting || loadingCustomers}
                                    title="Reload customers"
                                    aria-label="Reload customers">
                                <span className="icon is-small">
                                    <i className={clsx('fas fa-sync-alt', loadingCustomers && 'fa-spin')} />
                                </span>
                            </button>
                        </div>
                    </div>
                    {!loadingCustomers && customerOptions.length === 0 &&
                        <p className={clsx('help is-warning', classes.CustomerEmptyNotice)}>No existing customers found for this partner.</p>}
                </div>}
                {!isExistingCustomerOrder && <>
                    <div className="field">
                        <label className="label">First name *</label>
                        <p className="control has-icons-left">
                            <input className={clsx('input is-small', firstName.length > 0 && 'is-success')} type="text"
                                   placeholder="Customer first name" value={firstName}
                                   disabled={isSubmitting}
                                   onChange={handleSetFirstName} />
                            <span className="icon is-small is-left">
                                <i className="fas fa-id-card" />
                            </span>
                        </p>
                    </div>
                    <div className="field">
                        <label className="label">Last name</label>
                        <p className="control has-icons-left">
                            <input className="input is-small" type="text" placeholder="Customer last name" value={lastName}
                                   disabled={isSubmitting}
                                   onChange={handleSetLastName} />
                            <span className="icon is-small is-left">
                                <i className="fas fa-id-card" />
                            </span>
                        </p>
                    </div>
                    <div className="field">
                        <label className="label">Company *</label>
                        <p className="control has-icons-left">
                            <input className={clsx('input is-small', company.length > 0 && 'is-success')} type="text"
                                   placeholder="Customer company" value={company}
                                   disabled={isSubmitting}
                                   onChange={handleSetCompany} />
                            <span className="icon is-small is-left">
                                <i className="fas fa-building" />
                            </span>
                        </p>
                    </div>
                    {isBase && <>
                        <hr />
                        <h5>Direct order info</h5>
                        <div className="field">
                            <label className="label">VAT Number *</label>
                            <p className="control has-icons-left">
                                <input className={clsx('input is-small', vat.length > 0 && 'is-success')} type="text"
                                       placeholder="Customer VAT" value={vat}
                                       disabled={isSubmitting}
                                       onChange={handleSetVat} />
                                <span className="icon is-small is-left">
                                <i className="fas fa-building" />
                            </span>
                            </p>
                        </div>
                        <div className="field">
                            <label className="label">Address *</label>
                            <p className="control has-icons-left">
                                <input className={clsx('input is-small', address.length > 0 && 'is-success')} type="text"
                                       placeholder="Customer address" value={address}
                                       disabled={isSubmitting}
                                       onChange={handleSetAddress} />
                                <span className="icon is-small is-left">
                                <i className="fas fa-building" />
                            </span>
                            </p>
                        </div>
                        <div className="field">
                            <label className="label">City *</label>
                            <p className="control has-icons-left">
                                <input className={clsx('input is-small', city.length > 0 && 'is-success')} type="text"
                                       placeholder="Customer city and state/province" value={city}
                                       disabled={isSubmitting}
                                       onChange={handleSetCity} />
                                <span className="icon is-small is-left">
                                <i className="fas fa-building" />
                            </span>
                            </p>
                        </div>
                        <div className="field">
                            <label className="label">CAP *</label>
                            <p className="control has-icons-left">
                                <input className={clsx('input is-small', cap.length > 0 && 'is-success')} type="text"
                                       placeholder="Customer CAP" value={cap}
                                       disabled={isSubmitting}
                                       onChange={handleSetCap} />
                                <span className="icon is-small is-left">
                                <i className="fas fa-building" />
                            </span>
                            </p>
                        </div>
                        <hr />
                    </>}
                    <div className="field">
                        <label className="label">Customer email (AIDA username)*</label>
                        <p className="control has-icons-left">
                            <input className={clsx('input is-small', usernameIsValid ? 'is-success' : 'is-error')} type="email"
                                   placeholder="Customer email" value={username}
                                   autoComplete="off"
                                   data-bwignore="true"
                                   data-lpignore="true"
                                   disabled={isSubmitting}
                                   onChange={handleSetUsername} />
                            <span className="icon is-small is-left">
                                <i className="fas fa-envelope" />
                            </span>
                        </p>
                        {usernameError && <p className="help is-danger">{usernameError}</p>}
                    </div>
                    <div className="field">
                        <label className="label">Country *</label>
                        <div className="control has-icons-left">
                            <div className={clsx('select is-small is-block', country.length > 0 && 'is-success')}>
                                <select value={country} onChange={handleSetCountry} disabled={isSubmitting}>
                                    <option value="">---</option>
                                    {getCountries().map(o => <option key={`country-${o.value}`}
                                                                     value={o.value}>{o.displayValue}</option>)}
                                </select>
                                <div className="icon is-small is-left">
                                    <i className="fas fa-globe" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="field">
                        <label className="label">Language *</label>
                        <div className="control has-icons-left">
                            <div className={clsx('select is-small is-block', language.length > 0 && 'is-success')}>
                                <select value={language} onChange={handleSetLanguage} disabled={isSubmitting}>
                                    <option value="">---</option>
                                    {getLanguages().map(o => <option key={`country-${o.value}`}
                                                                     value={o.value}>{o.displayValue}</option>)}
                                </select>
                                <div className="icon is-small is-left">
                                    <i className="fas fa-language" />
                                </div>
                            </div>
                        </div>
                    </div>
                </>}
                <div className="field">
                    <label className="label">Order reference</label>
                    <p className="control has-icons-left">
                        <input className="input is-small" type="text" placeholder="Your internal order reference"
                               value={orderRef}
                               disabled={isSubmitting}
                               onChange={handleSetOrderRef} />
                        <span className="icon is-small is-left">
                                <i className="fas fa-hashtag" />
                            </span>
                    </p>
                </div>
                <div className="field">
                    <label className="label">Order note</label>
                    <p className="control">
                        <input className="input is-small" type="text"
                               placeholder="Anything you need to specify about this order"
                               value={orderNote}
                               disabled={isSubmitting}
                               onChange={handleSetOrderNote} />
                    </p>
                </div>
                <Confirm
                    body="Are you sure you want to confirm and send this order?"
                    onConfirm={handleGenerate}
                >
                    <button
                        style={{ marginTop: '10px' }}
                        className={clsx('button', 'is-primary', isSubmitting && 'is-loading')}
                        disabled={isSubmitting || price === 0 || isCalculating || !customerInfoIsValid || !thirdpartyIsValid}
                    >
                        Generate order
                    </button>
                </Confirm>
            </>}
        </>}
    </div>;
};

export default Summary;
